'use client';

import { $getNodeByKey } from 'lexical';
import { $isBeautifulMentionNode } from 'lexical-beautiful-mentions';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Portal } from '@ark-ui/react';
import { useTranslations } from 'next-intl';
import { Box, VStack } from '@terra/ui-styled-system';
import { HoverCard } from '../../../../../components/hover-card';
import { MetaDefault } from '../../../../../components/typography';
import { useMentions } from '../../../mentions/ChatMentionsProvider';
import { MentionRemoveButton } from './MentionRemoveButton';
import type { MentionOverflowHiddenItem } from './mentionOverflow';
import { useState, type MouseEvent } from 'react';

type MentionOverflowBadgeProps = {
  overflowMentions: MentionOverflowHiddenItem[];
};

export function MentionOverflowBadge({
  overflowMentions,
}: MentionOverflowBadgeProps) {
  const t = useTranslations('designSystem.chat.mentionOverflow');
  const [editor] = useLexicalComposerContext();
  const { removeMention, removeMentionByEntityId } = useMentions();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const count = overflowMentions.length;

  const handleRemove = ({
    event,
    mention,
  }: {
    event: MouseEvent<HTMLButtonElement>;
    mention: MentionOverflowHiddenItem;
  }) => {
    event.preventDefault();
    event.stopPropagation();

    if (mention.mentionMenuItemId) {
      removeMention(mention.mentionMenuItemId);
    } else if (mention.entityId) {
      removeMentionByEntityId(mention.entityId);
    }

    editor.update(() => {
      const node = $getNodeByKey(mention.key);
      if ($isBeautifulMentionNode(node)) {
        node.remove();
      }
    });
  };

  return (
    <HoverCard.Root
      openDelay={100}
      closeDelay={100}
      positioning={{ placement: 'top' }}
    >
      <HoverCard.Trigger asChild>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="sm"
          borderWidth="1px"
          borderColor="terra.a3"
          bg="terra.a1"
          color="terra.11"
          fontSize="xs"
          fontWeight="medium"
          px={1.5}
          py={0.5}
          mx={0.5}
          verticalAlign="middle"
          cursor="default"
          _hover={{ bg: 'terra.a2', borderColor: 'terra.a4' }}
          aria-label={t('moreMentionsAria', { count })}
        >
          <MetaDefault color="terra.11">+{count}</MetaDefault>
        </Box>
      </HoverCard.Trigger>
      <Portal>
        <HoverCard.Positioner>
          <HoverCard.Content
            p={1}
            minW="160px"
            maxW="280px"
            maxH="240px"
            overflowY="auto"
          >
            <VStack alignItems="stretch" gap={0.5}>
              {overflowMentions.map((mention) => (
                <Box
                  key={mention.key}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  px={2}
                  py={1}
                  borderRadius="sm"
                  onMouseEnter={() => setHoveredKey(mention.key)}
                  onMouseLeave={() => setHoveredKey(null)}
                  _hover={{ bg: 'bg.subtle' }}
                >
                  <MetaDefault
                    color="fg.default"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {mention.label}
                  </MetaDefault>
                  {hoveredKey === mention.key ? (
                    <MentionRemoveButton
                      label={mention.label}
                      onRemove={(event) => handleRemove({ event, mention })}
                    />
                  ) : null}
                </Box>
              ))}
            </VStack>
          </HoverCard.Content>
        </HoverCard.Positioner>
      </Portal>
    </HoverCard.Root>
  );
}
